import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useStore, Environment, Variable } from '../../stores';
import { X, Plus, Globe, Box, Edit2, Copy, Trash2 } from 'lucide-react';
import { VariableEditor } from './VariableEditor';
import { useEnvironments } from '../../hooks/useEnvironments';

interface EnvironmentManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialEnvironmentId?: string | null;
}

interface EnvironmentFormData {
  name: string;
  isGlobal: boolean;
  variables: Variable[];
}

const emptyVariable = (): Variable => ({
  key: '',
  value: '',
  isSecret: false,
  enabled: true,
});

const emptyFormData = (): EnvironmentFormData => ({
  name: '',
  isGlobal: false,
  variables: [emptyVariable()],
});

export function EnvironmentManager({ open, onOpenChange, initialEnvironmentId }: EnvironmentManagerProps) {
  const {
    environments,
    createEnvironment,
    updateEnvironment,
    deleteEnvironment: hookDeleteEnvironment,
    duplicateEnvironment,
  } = useEnvironments();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<EnvironmentFormData>(emptyFormData());
  const [isCreating, setIsCreating] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Handle initial environment to edit
  useEffect(() => {
    if (open && initialEnvironmentId) {
      const env = environments.find((e) => e.id === initialEnvironmentId);
      if (env) {
        handleEdit(env);
      }
    }
  }, [open, initialEnvironmentId]);

  // Reset state when closing
  useEffect(() => {
    if (!open) {
      setEditingId(null);
      setFormData(emptyFormData());
      setIsCreating(false);
      setDeleteConfirmId(null);
    }
  }, [open]);

  const handleEdit = (env: Environment) => {
    setEditingId(env.id);
    setFormData({
      name: env.name,
      isGlobal: env.isGlobal,
      variables: env.variables.length > 0 ? [...env.variables] : [emptyVariable()],
    });
    setIsCreating(false);
  };

  const handleCreate = () => {
    setEditingId(null);
    setFormData(emptyFormData());
    setIsCreating(true);
  };

  const handleDuplicate = (env: Environment) => {
    duplicateEnvironment(env);
  };

  const handleSave = async () => {
    // Filter out empty variables
    const filteredVariables = formData.variables.filter(
      (v) => v.key.trim() !== '' || v.value.trim() !== ''
    );

    if (isCreating) {
      await createEnvironment({
        name: formData.name || 'New Environment',
        isGlobal: formData.isGlobal,
        variables: filteredVariables,
      });
      setIsCreating(false);
      setFormData(emptyFormData());
    } else if (editingId) {
      await updateEnvironment(editingId, {
        name: formData.name || 'Environment',
        isGlobal: formData.isGlobal,
        variables: filteredVariables,
      });
      setEditingId(null);
      setFormData(emptyFormData());
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData(emptyFormData());
    setIsCreating(false);
  };

  const handleDelete = async (id: string) => {
    await hookDeleteEnvironment(id);
    setDeleteConfirmId(null);
    if (editingId === id) {
      setEditingId(null);
      setFormData(emptyFormData());
    }
  };

  const isEditing = editingId !== null || isCreating;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card border border-border rounded-lg shadow-xl z-50 w-[700px] max-h-[85vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <Dialog.Title className="text-lg font-semibold">
              {isCreating ? 'Create Environment' : editingId ? 'Edit Environment' : 'Manage Environments'}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="p-1 rounded hover:bg-accent transition-colors">
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </Dialog.Close>
          </div>

          <div className="flex-1 overflow-auto">
            {!isEditing ? (
              // List View
              <div className="p-4">
                <div className="flex justify-between items-center mb-4">
                  <p className="text-sm text-muted-foreground">
                    {environments.length} environment{environments.length !== 1 ? 's' : ''}
                  </p>
                  <button
                    onClick={handleCreate}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    New Environment
                  </button>
                </div>

                {environments.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Box className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg mb-2">No environments yet</p>
                    <p className="text-sm">Create an environment to store variables for your requests.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {environments.map((env) => (
                      <div
                        key={env.id}
                        className="flex items-center gap-3 p-3 bg-background border border-border rounded-lg group hover:border-primary/50 transition-colors"
                      >
                        {env.isGlobal ? (
                          <Globe className="w-5 h-5 text-blue-500 flex-shrink-0" />
                        ) : (
                          <Box className="w-5 h-5 text-green-500 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{env.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {env.variables.length} variable{env.variables.length !== 1 ? 's' : ''}
                            {env.isGlobal && ' • Global'}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleEdit(env)}
                            className="p-1.5 rounded hover:bg-accent transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4 text-muted-foreground" />
                          </button>
                          <button
                            onClick={() => handleDuplicate(env)}
                            className="p-1.5 rounded hover:bg-accent transition-colors"
                            title="Duplicate"
                          >
                            <Copy className="w-4 h-4 text-muted-foreground" />
                          </button>
                          {deleteConfirmId === env.id ? (
                            <div className="flex items-center gap-1 ml-2">
                              <button
                                onClick={() => handleDelete(env.id)}
                                className="px-2 py-1 text-xs bg-destructive text-destructive-foreground rounded hover:bg-destructive/90"
                              >
                                Delete
                              </button>
                              <button
                                onClick={() => setDeleteConfirmId(null)}
                                className="px-2 py-1 text-xs bg-muted rounded hover:bg-accent"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirmId(env.id)}
                              className="p-1.5 rounded hover:bg-destructive/10 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              // Edit/Create View
              <div className="p-4 space-y-4">
                {/* Environment Name */}
                <div>
                  <label className="block text-sm font-medium mb-1.5">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Environment name"
                    className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    autoFocus
                  />
                </div>

                {/* Global Toggle */}
                <div className="flex items-center gap-3">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isGlobal}
                      onChange={(e) => setFormData((prev) => ({ ...prev, isGlobal: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-muted rounded-full peer peer-checked:bg-primary transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
                  </label>
                  <div>
                    <p className="text-sm font-medium">Global Environment</p>
                    <p className="text-xs text-muted-foreground">Variables available in all workspaces</p>
                  </div>
                </div>

                {/* Variables */}
                <div>
                  <label className="block text-sm font-medium mb-2">Variables</label>
                  <div className="border border-border rounded-md p-3 bg-background/50">
                    <VariableEditor
                      variables={formData.variables}
                      onChange={(variables) => setFormData((prev) => ({ ...prev, variables }))}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {isEditing && (
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-border">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm rounded-md hover:bg-accent transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                disabled={!formData.name.trim()}
              >
                {isCreating ? 'Create Environment' : 'Save Changes'}
              </button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
